import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Input,
  Text,
} from '@chakra-ui/react'
import { capitalize, omit } from 'lodash'
import React from 'react'
import type { Abi, AbiFunction, Address } from 'viem'
import { getContract } from 'viem'
import { useAccount, useSwitchChain } from 'wagmi'
import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { TUPLE_TYPE } from 'constants/common'
import { zircuit } from 'constants/network'
import { useThemeColors } from 'hooks/useThemeColors'
import { publicClientL2, createWalletClientL2 } from 'lib/viem'
import { getParsedContractArgument } from 'utils/getParsedContractArgument'

type ReadContractProps = {
  title?: React.ReactNode
  data: AbiFunction[]
  hash: string
}

// Response can be also `tuple` - array of values.
const getParsedResponse = (
  response: unknown[] | unknown[][],
  outputs: AbiFunction['outputs'] = []
) => {
  try {
    if (outputs.length === 1) {
      const [{ type }] = outputs

      if (type === TUPLE_TYPE) {
        return [{ type, value: (response as unknown[]).join(',') }]
      }
      return [{ type, value: response }]
    }

    return outputs.map(({ type }, index) => ({
      type,
      value: type === TUPLE_TYPE ? (response[index] as unknown[]).join(',') : response[index],
    }))
  } catch {
    return null
  }
}

export const ReadContract = ({ title, data, hash }: ReadContractProps) => {
  const COLORS = useThemeColors()
  const [inputStates, setInputStates] = React.useState<Record<number, Record<number, string>>>({})
  const [responses, setResponses] = React.useState<
    Record<number, { type: string; value: unknown }[]>
  >({})
  const [errors, setErrors] = React.useState<Record<number, string>>({})
  const [loadingQueryIndexes, setLoadingQueryIndexes] = React.useState<number[]>([])

  const { address, chainId } = useAccount()
  const { switchChain } = useSwitchChain()

  const isCorrectNetwork = chainId === zircuit.id

  return (
    <>
      {title}
      {data.length > 0 ? (
        <Accordion allowMultiple>
          {data.map(({ inputs, name, outputs }, methodIndex) => (
            <AccordionItem
              key={`${name ?? ''}-${methodIndex.toString()}-read`}
              bgColor={COLORS.grey07}
              _notFirst={{ marginTop: 4 }}
              border="none"
              borderRadius={8}
            >
              <AccordionButton p={4} borderRadius={8}>
                <Text w="full" textAlign="start" variant="title2medium">
                  {methodIndex + 1}. {name}
                </Text>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Divider mb={3} />
                {inputs?.map(({ name: inputName, type }, inputIndex) => {
                  const inputNameAndType = `${inputName || '<input>'} (${type})`

                  return (
                    <Box
                      my={4}
                      key={`${inputName ?? ''}-${type ?? ''}-${inputIndex.toString()}-read`}
                    >
                      <Text mb={2} variant="caption1regular" color={COLORS.grey02}>
                        {inputNameAndType}
                      </Text>
                      <Input
                        value={inputStates[methodIndex]?.[inputIndex] ?? ''}
                        w="65%"
                        onChange={(event) =>
                          setInputStates((prev) => ({
                            ...prev,
                            [methodIndex]: {
                              ...prev[methodIndex],
                              [inputIndex]: event.target.value,
                            },
                          }))
                        }
                        placeholder={inputNameAndType}
                      />
                    </Box>
                  )
                })}
                {responses[methodIndex] ? (
                  <Alert mb={4} w="fit-content" variant="success">
                    Method response:{' '}
                    {responses[methodIndex]?.map(({ type, value }, index) => {
                      const responseLengthIndex = responses[methodIndex].length - 1

                      return (
                        <Text
                          key={`${type ?? ''}-${index.toString()}-read`}
                          as="span"
                          variant="caption2regular"
                          color="inherit"
                        >
                          {type}: {String(value)}
                          {responseLengthIndex > 0 && index !== responseLengthIndex && ', '}
                        </Text>
                      )
                    })}
                  </Alert>
                ) : (
                  outputs && (
                    <Text mb={4} variant="caption2regular" color={COLORS.success}>
                      {outputs.map(({ type }) => type).join(', ')}
                    </Text>
                  )
                )}
                {errors[methodIndex] && (
                  <Alert mb={4} w="fit-content" variant="error">
                    {errors[methodIndex]}
                  </Alert>
                )}
                <Button
                  isLoading={loadingQueryIndexes.includes(methodIndex)}
                  onClick={() => {
                    const asyncFn = async () => {
                      try {
                        const args = Object.values(inputStates[methodIndex] ?? {}).map(
                          (arg: string) => {
                            // Handle array inputs that start and end with brackets
                            if (arg.trim().startsWith('[') && arg.trim().endsWith(']')) {
                              return arg
                                .trim()
                                .slice(1, -1)
                                .split(',')
                                .map((item) => getParsedContractArgument(item))
                            }

                            return getParsedContractArgument(arg)
                          }
                        )

                        setLoadingQueryIndexes((prev) => [...prev, methodIndex])

                        if (name) {
                          const contract = getContract({
                            address: hash as Address,
                            abi: data as Abi,
                            client: address ? (await createWalletClientL2())! : publicClientL2,
                          })

                          const result = await contract.read[name](
                            args.filter((value) => value !== '')
                          )

                          setErrors((prev) => omit(prev, methodIndex))
                          setResponses((prev) => ({
                            ...prev,
                            [methodIndex]: getParsedResponse([result], outputs) ?? [],
                          }))
                        } else {
                          throw new Error('No method name provided')
                        }
                      } catch (error) {
                        if (error instanceof Error) {
                          setErrors((prev) => ({
                            ...prev,
                            [methodIndex]:
                              'shortMessage' in error && typeof error.shortMessage === 'string'
                                ? capitalize(error.shortMessage)
                                : error.message,
                          }))
                        }
                      } finally {
                        setLoadingQueryIndexes((prev) =>
                          prev.filter((queryIndex) => queryIndex !== methodIndex)
                        )
                      }
                    }

                    if (!isCorrectNetwork) {
                      setLoadingQueryIndexes((prev) => [...prev, methodIndex])
                      switchChain(
                        {
                          chainId: zircuit.id,
                        },
                        {
                          onSettled: (_, error) => {
                            if (!error) {
                              void asyncFn()
                            } else {
                              setLoadingQueryIndexes((prev) =>
                                prev.filter((queryIndex) => queryIndex !== methodIndex)
                              )
                            }
                          },
                        }
                      )
                    } else {
                      void asyncFn()
                    }
                  }}
                  size="small"
                >
                  Query
                </Button>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <Alert variant="information">
          The ABI for this smart contract does not contain any read methods.
        </Alert>
      )}
    </>
  )
}
